import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import CapabilityStatementModal from "@/components/CapabilityStatementModal";
import { 
  Wrench, 
  Truck, 
  Settings, 
  Home as HomeIcon, 
  PenTool,
  ArrowRight,
  ArrowDown,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  FileText,
  ThumbsUp,
  Package,
  Download
} from "lucide-react";

const HERO_IMAGE = "/sea_container_4.jpg";

const services = [
  {
    icon: HomeIcon,
    title: "Livestock Infrastructure",
    description: "Heavy-duty shelters, yards, and livestock handling equipment.",
    id: "livestock-infrastructure"
  },
  {
    icon: Wrench,
    title: "Welding & Fabrication",
    description: "Custom metal fabrication and welding services for all applications.",
    id: "welding-fabrication"
  },
  {
    icon: Settings,
    title: "Custom Attachments",
    description: "Loader buckets, forks, and machinery attachments built to spec.",
    id: "custom-attachments"
  },
  {
    icon: PenTool,
    title: "CAD Design",
    description: "Professional design services for custom engineered solutions.",
    id: "cad-design"
  },
  {
    icon: Truck,
    title: "Mobile Onsite Welding",
    description: "We come to you for repairs and fabrication work on-site.",
    id: "mobile-welding"
  }
];

const featuredProducts = [
  {
    id: "livestock-shelters",
    title: "4-5m Livestock Shelter",
    category: "Infrastructure",
    price: "From $4,500 + GST",
    images: ["/IMG_6286_shelter_main.jpeg", "/IMG_6296.jpeg", "/shelter_normal.jpg", "/shelter_new1.jpg", "/shelter_new2.jpg"]
  },
  {
    id: "sea-container-lean-to",
    title: "Sea Container Lean-To",
    category: "Infrastructure",
    price: "Price on Request",
    images: ["/sea_container_1.jpg", "/sea_container_2.jpg", "/sea_container_3.jpg", "/sea_container_4.jpg"]
  },
  {
    id: "custom-sheds",
    title: "Custom Sheds",
    category: "Infrastructure",
    price: "Price on Request",
    images: ["/sea_container_6.jpg", "/sea_container_transparent_roof.jpg", "/sea_container_5.jpg", "/livestock_shelter_yards.jpg", "/livestock_shelter_green.jpg"]
  }
];

// Clean Professional Image Carousel with smooth slide animation
function ProductCarousel({ images, name }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const containerRef = useRef(null);
  const touchDataRef = useRef({ startX: 0, startY: 0, endX: 0, isSwiping: false });
  const currentIndexRef = useRef(currentIndex);
  const isTransitioningRef = useRef(isTransitioning);

  // Keep refs in sync with state
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  useEffect(() => {
    isTransitioningRef.current = isTransitioning;
  }, [isTransitioning]);

  const minSwipeDistance = 30;

  const goToImage = (index) => {
    if (isTransitioningRef.current) return;
    setIsTransitioning(true);
    isTransitioningRef.current = true;
    setCurrentIndex(index);
    currentIndexRef.current = index;
    setTimeout(() => {
      setIsTransitioning(false);
      isTransitioningRef.current = false;
    }, 300);
  };

  const nextImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentIndexRef.current < images.length - 1) {
      goToImage(currentIndexRef.current + 1);
    }
  };

  const prevImage = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (currentIndexRef.current > 0) {
      goToImage(currentIndexRef.current - 1);
    }
  };

  // Use native event listeners for touch to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      touchDataRef.current = {
        startX: e.touches[0].clientX,
        startY: e.touches[0].clientY,
        endX: e.touches[0].clientX,
        isSwiping: false
      };
    };

    const handleTouchMove = (e) => {
      const { startX, startY } = touchDataRef.current;
      const currentX = e.touches[0].clientX;
      const currentY = e.touches[0].clientY;
      const diffX = Math.abs(startX - currentX);
      const diffY = Math.abs(startY - currentY);

      // If horizontal movement is greater than vertical, prevent page scroll
      if (diffX > diffY && diffX > 10) {
        e.preventDefault();
        touchDataRef.current.isSwiping = true;
      }
      touchDataRef.current.endX = currentX;
    };

    const handleTouchEnd = () => {
      const { startX, endX } = touchDataRef.current;
      const distance = startX - endX;
      
      if (Math.abs(distance) > minSwipeDistance && images.length > 1) {
        if (distance > 0 && currentIndexRef.current < images.length - 1) {
          goToImage(currentIndexRef.current + 1);
        } else if (distance < 0 && currentIndexRef.current > 0) {
          goToImage(currentIndexRef.current - 1);
        }
      }
      touchDataRef.current.isSwiping = false;
    };

    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [images.length]); // Only re-attach if images array length changes

  if (!images || images.length === 0) {
    return (
      <div className="aspect-[4/3] overflow-hidden bg-[#1A1A1A] flex items-center justify-center rounded-t-2xl">
        <p className="text-[#A3A3A3] text-sm">Contact for details</p>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="aspect-[4/3] overflow-hidden relative group bg-[#0A0A0A] rounded-t-2xl"
      style={{ touchAction: 'pan-y' }}
    >
      {/* Images Container - Horizontal slide */}
      <div 
        className="flex h-full transition-transform duration-300 ease-out"
        style={{ transform: `translateX(-${currentIndex * 100}%)` }}
      >
        {images.map((img, idx) => (
          <img 
            key={idx}
            src={img}
            alt={`${name} - Image ${idx + 1}`}
            className="w-full h-full object-cover flex-shrink-0 select-none pointer-events-none"
            draggable="false"
          />
        ))}
      </div>
      
      {/* Swipe hint for mobile */}
      {images.length > 1 && (
        <div className="absolute top-2 right-2 md:hidden">
          <div className="px-3 py-1.5 bg-black/70 backdrop-blur-sm rounded-full shadow-lg border border-white/10">
            <span className="text-white text-xs font-bold uppercase tracking-wider">Swipe</span>
          </div>
        </div>
      )}
      
      {/* Gradient overlay */}
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
      
      {/* Navigation Controls */}
      {images.length > 1 && (
        <>
          {/* Left Arrow - Desktop only */}
          <button
            onClick={prevImage}
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full hidden md:flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 border border-white/20"
            aria-label="Previous image"
          >
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>
          
          {/* Right Arrow - Desktop only */}
          <button
            onClick={nextImage}
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-white/10 backdrop-blur-sm hover:bg-white/20 rounded-full hidden md:flex items-center justify-center transition-all duration-200 opacity-0 group-hover:opacity-100 border border-white/20"
            aria-label="Next image"
          >
            <ChevronRight className="w-4 h-4 text-white" />
          </button>
          
          {/* Image Counter & Progress */}
          <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
            <span className="px-2 py-0.5 bg-black/50 backdrop-blur-sm rounded-full text-white text-xs font-medium">
              {currentIndex + 1}/{images.length}
            </span>
            
            <div className="flex gap-1">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    goToImage(idx);
                  }}
                  className={`h-1 rounded-full transition-all duration-200 ${
                    idx === currentIndex 
                      ? 'bg-[#E63946] w-4' 
                      : 'bg-white/40 w-1 hover:bg-white/70'
                  }`}
                  aria-label={`Go to image ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Custom hook for scroll animation
function useScrollAnimation() {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );

    const elements = ref.current?.querySelectorAll(".animate-on-scroll");
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return ref;
}

export default function HomePage() {
  const containerRef = useScrollAnimation();
  const [isCapabilityModalOpen, setIsCapabilityModalOpen] = useState(false);

  return (
    <div className="page-transition" data-testid="home-page" ref={containerRef}>
      <Helmet>
        <title>PWC Fabrication and Engineering Great Southern | Horse Shelters & Stick Rakes Albany WA</title>
        <meta name="description" content="PWC Fabrication and Engineering Great Southern - Quality horse shelters, livestock shelters, stick rakes & custom metal fabrication in Albany & Great Southern Western Australia. Mobile welding services. Call 0402 275 003." />
        <meta name="keywords" content="PWC, PWC Fabrication, PWC Engineering, horse shelters, horse shelters great southern, horse shelters Albany, stick rakes, stick rake attachments, fabrication Albany, fabrication Great Southern, welding Albany, custom fabrication WA, mobile welding Great Southern" />
        <link rel="canonical" href="https://pwcengineering.com.au" />
      </Helmet>
      
      {/* Capability Statement Modal */}
      <CapabilityStatementModal 
        isOpen={isCapabilityModalOpen} 
        onClose={() => setIsCapabilityModalOpen(false)} 
      />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 industrial-texture" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <div className="max-w-3xl">
            <span className="inline-block text-[#E63946] font-semibold uppercase tracking-widest mb-6 text-sm animate-on-scroll relative">
              <span className="absolute -inset-2 bg-[#E63946]/20 blur-xl rounded-full"></span>
              <span className="relative">Australian Engineering Excellence</span>
            </span>
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight uppercase text-white leading-tight mb-6 animate-on-scroll delay-100">
              Precision Fabrication.<br />
              <span className="text-[#E63946]">Built to Last.</span>
            </h1>
            <p className="text-xl text-[#E5E5E5] mb-10 leading-relaxed max-w-xl animate-on-scroll delay-200">
              Custom metal fabrication, mobile welding, and engineered solutions 
              for farmers, contractors, and equipment owners across the Great Southern.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-on-scroll delay-300">
              <Link to="/contact">
                <Button 
                  className="bg-[#E63946] hover:bg-[#D62839] text-white font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-full btn-glow"
                  data-testid="hero-quote-btn"
                >
                  Request a Quote
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              <Link to="/services">
                <Button 
                  variant="outline"
                  className="border-2 border-white/30 hover:border-[#E63946] bg-transparent text-white font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-full hover:bg-white/5"
                  data-testid="hero-services-btn"
                >
                  Our Services
                </Button>
              </Link>
            </div>
            
            {/* Capability Statement Button */}
            <div className="mt-6 animate-on-scroll delay-400">
              <button
                onClick={() => setIsCapabilityModalOpen(true)}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-black border border-white/20 hover:border-black text-white px-5 py-2.5 rounded-full transition-all duration-300 group"
                data-testid="capability-statement-btn"
              >
                <Download className="w-4 h-4 group-hover:animate-bounce" />
                <span className="text-sm font-semibold uppercase tracking-wider">Download Capability Statement</span>
              </button>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div 
            className="w-7 h-12 rounded-full flex justify-center scroll-indicator-red"
          >
            <div className="w-1.5 h-4 rounded-full mt-2 animate-pulse scroll-dot-red" />
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="what-we-offer-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-[#E63946] font-semibold uppercase tracking-widest mb-4 text-sm animate-on-scroll">
                What We Offer
              </span>
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6 animate-on-scroll delay-100">
                Complete Fabrication Services
              </h2>
              <p className="text-lg text-[#A3A3A3] leading-relaxed mb-8 animate-on-scroll delay-200">
                From concept to completion, we handle every aspect of your project. 
                Our services include:
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Custom metal fabrication and welding",
                  "Loader buckets and machinery attachments",
                  "Livestock shelters, yards, and handling equipment",
                  "CAD design and engineering consultation",
                  "4x4 and automotive fabrication",
                  "Structural steel work",
                  "Mobile onsite welding and repairs"
                ].map((item, index) => (
                  <li key={index} className={`flex items-start gap-3 animate-on-scroll delay-${(index + 3) * 100}`}>
                    <CheckCircle2 className="text-[#E63946] flex-shrink-0 mt-1" size={20} />
                    <span className="text-[#F5F5F5]">{item}</span>
                  </li>
                ))}
              </ul>
              <button 
                onClick={() => {
                  document.getElementById('services-section')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="border-2 border-[#2A2A2A] hover:border-[#E63946] bg-transparent text-white font-bold uppercase tracking-wider px-8 py-4 rounded-full animate-on-scroll inline-flex items-center transition-colors"
                data-testid="view-services-btn"
              >
                Scroll to See Services
                <ArrowDown className="ml-2" size={18} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4 animate-on-scroll">
              <div className="aspect-[3/4] bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl overflow-hidden">
                <img 
                  src="/shelter_interior.jpg"
                  alt="Livestock shelter interior"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="aspect-[3/4] bg-[#1A1A1A] border border-[#2A2A2A] rounded-2xl overflow-hidden mt-8">
                <img 
                  src="/sea_container_6.jpg"
                  alt="Sea Container Lean-To Shelter"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section id="services-section" className="py-20 md:py-32 bg-[#121212]" data-testid="services-overview-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#E63946] font-semibold uppercase tracking-widest mb-4 text-sm animate-on-scroll">
              What We Do
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white animate-on-scroll delay-100">
              Our Services
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 3).map((service, index) => (
              <Link 
                key={index}
                to={`/services#${service.id}`}
                className={`service-card group bg-[#1A1A1A] border border-[#2A2A2A] p-6 md:p-8 card-hover hover:border-[#E63946]/50 block animate-on-scroll delay-${(index % 3 + 1) * 100} relative`}
                data-testid={`service-card-${index}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#E63946]/20 to-[#E63946]/5 rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <service.icon className="service-icon w-7 h-7 md:w-8 md:h-8 text-[#E63946]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg md:text-xl font-bold uppercase tracking-wide text-white mb-2 group-hover:text-[#E63946] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-[#A3A3A3] text-sm md:text-base leading-relaxed line-clamp-2 md:line-clamp-none">
                      {service.description}
                    </p>
                  </div>
                  {/* Pulse Indicator - visible on all screen sizes */}
                  <div className="flex items-center flex-shrink-0">
                    <div className="relative w-6 h-6 flex items-center justify-center">
                      {/* Outer ring */}
                      <div className="absolute w-6 h-6 border-2 border-[#E63946]/40 rounded-full animate-pulse-ring"></div>
                      {/* Middle ring */}
                      <div className="absolute w-4 h-4 border-2 border-[#E63946]/60 rounded-full animate-pulse-ring" style={{animationDelay: '0.2s'}}></div>
                      {/* Inner dot */}
                      <div className="w-2 h-2 bg-[#E63946] rounded-full"></div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          
          {/* Bottom 2 services - centered on desktop */}
          <div className="grid md:grid-cols-2 gap-6 mt-6 lg:max-w-[66%] lg:mx-auto">
            {services.slice(3).map((service, index) => (
              <Link 
                key={index + 3}
                to={`/services#${service.id}`}
                className={`service-card group bg-[#1A1A1A] border border-[#2A2A2A] p-6 md:p-8 card-hover hover:border-[#E63946]/50 block animate-on-scroll delay-${((index + 3) % 3 + 1) * 100} relative`}
                data-testid={`service-card-${index + 3}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-gradient-to-br from-[#E63946]/20 to-[#E63946]/5 rounded-xl md:rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <service.icon className="service-icon w-7 h-7 md:w-8 md:h-8 text-[#E63946]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg md:text-xl font-bold uppercase tracking-wide text-white mb-2 group-hover:text-[#E63946] transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-[#A3A3A3] text-sm md:text-base leading-relaxed line-clamp-2 md:line-clamp-none">
                      {service.description}
                    </p>
                  </div>
                  {/* Pulse Indicator - visible on all screen sizes */}
                  <div className="flex items-center flex-shrink-0">
                    <div className="relative w-6 h-6 flex items-center justify-center">
                      {/* Outer ring */}
                      <div className="absolute w-6 h-6 border-2 border-[#E63946]/40 rounded-full animate-pulse-ring"></div>
                      {/* Middle ring */}
                      <div className="absolute w-4 h-4 border-2 border-[#E63946]/60 rounded-full animate-pulse-ring" style={{animationDelay: '0.2s'}}></div>
                      {/* Inner dot */}
                      <div className="w-2 h-2 bg-[#E63946] rounded-full"></div>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12 animate-on-scroll">
            <Link to="/services">
              <Button 
                variant="outline"
                className="border-2 border-[#2A2A2A] hover:border-[#E63946] bg-transparent text-white font-bold uppercase tracking-wider px-8 py-4 rounded-full hover:bg-[#E63946]/5"
                data-testid="view-all-services-btn"
              >
                View All Services
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Our Products */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="featured-products-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="inline-block text-[#E63946] font-semibold uppercase tracking-widest mb-4 text-sm animate-on-scroll">
              Our Products
            </span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white animate-on-scroll delay-100">
              Featured Products
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {featuredProducts.map((product, index) => (
              <Link 
                key={product.id}
                to={`/products#${product.id}`}
                className={`group bg-[#121212] border border-[#2A2A2A] rounded-2xl overflow-hidden card-hover hover:border-[#E63946]/50 block animate-on-scroll delay-${(index + 1) * 100}`}
                data-testid={`featured-product-${product.id}`}
              >
                <ProductCarousel images={product.images} name={product.title} />
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="inline-block px-3 py-1 bg-[#E63946]/10 rounded-full text-[#E63946] text-xs uppercase tracking-wider">
                      {product.category}
                    </span>
                    <span className="text-[#E63946] font-bold text-sm">
                      {product.price}
                    </span>
                  </div>
                  <h3 className="text-xl font-bold uppercase tracking-wide text-white group-hover:text-[#E63946] transition-colors">
                    {product.title}
                  </h3>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-12 animate-on-scroll">
            <Link to="/products">
              <Button 
                variant="outline"
                className="border-2 border-[#2A2A2A] hover:border-[#E63946] bg-transparent text-white font-bold uppercase tracking-wider px-8 py-4 rounded-full hover:bg-[#E63946]/5"
                data-testid="view-all-products-btn"
              >
                View All Products
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]" data-testid="our-process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-[#E63946] font-semibold uppercase tracking-widest mb-4 text-sm animate-on-scroll">
              Our Process
            </p>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6 animate-on-scroll delay-100">
              From Idea to Reality
            </h2>
            <p className="text-lg text-[#A3A3A3] leading-relaxed animate-on-scroll delay-200">
              We specialise in bespoke fabrication projects. Every custom build follows our 
              proven step-by-step process to ensure you get exactly what you need.
            </p>
          </div>

          {/* Process Steps */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
            {[
              {
                icon: Lightbulb,
                step: "01",
                title: "Idea",
                description: "Share your concept or problem with us."
              },
              {
                icon: PenTool,
                step: "02",
                title: "3D CAD Design",
                description: "We create detailed 3D CAD drawings."
              },
              {
                icon: FileText,
                step: "03",
                title: "Quote",
                description: "Receive a detailed quote upfront."
              },
              {
                icon: ThumbsUp,
                step: "04",
                title: "Approval",
                description: "Review and approve the design."
              },
              {
                icon: Package,
                step: "05",
                title: "Product",
                description: "We fabricate and deliver."
              }
            ].map((item, index) => (
              <div 
                key={index}
                className={`relative bg-[#121212] border border-[#2A2A2A] p-4 md:p-6 rounded-2xl text-center card-hover animate-on-scroll delay-${(index % 5 + 1) * 100} ${index === 4 ? 'col-span-2 md:col-span-1' : ''}`}
                data-testid={`process-step-${index}`}
              >
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 bg-[#E63946] rounded-full">
                  <span className="text-white text-xs font-bold">{item.step}</span>
                </div>
                <div className="w-12 h-12 md:w-14 md:h-14 bg-[#E63946]/10 border border-[#E63946]/30 rounded-xl mx-auto mb-3 md:mb-4 mt-3 flex items-center justify-center">
                  <item.icon className="w-6 h-6 md:w-7 md:h-7 text-[#E63946]" />
                </div>
                <h3 className="text-sm md:text-lg font-bold uppercase tracking-wide text-white mb-1 md:mb-2">
                  {item.title}
                </h3>
                <p className="text-[#A3A3A3] text-xs md:text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#E63946] relative overflow-hidden" data-testid="cta-section">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-black/10 rounded-full -translate-y-1/2 translate-x-1/3" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-black/10 rounded-full translate-y-1/2 -translate-x-1/3" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-6 animate-on-scroll">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto animate-on-scroll delay-100">
            Whether you need a custom build, emergency repair, or a quote for new equipment, 
            we're here to help. Get in touch today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-on-scroll delay-200">
            <Link to="/contact">
              <Button 
                className="bg-white hover:bg-gray-100 text-[#E63946] font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-full shadow-xl"
                data-testid="cta-quote-btn"
              >
                Request a Quote
              </Button>
            </Link>
            <a href="tel:0402275003">
              <Button 
                variant="outline"
                className="border-2 border-white bg-transparent hover:bg-white/10 text-white font-bold uppercase tracking-wider px-8 py-6 text-lg rounded-full"
                data-testid="cta-call-btn"
              >
                Call 0402 275 003
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
